import { designTokens, RiUserLine } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useState } from "react";
import { DealCardModal } from "~/components/common/deals/show/deal_card/DealCardModal";
import { EditMenuButton } from "~/components/common/parts/button/EditMenuButton";
import type { Deal } from "~/models/Deal";
import {
	getWitnessEmail,
	getWitnessName,
	getWitnessNameKana,
	getWitnessTel
} from "~/models/Deal";
import { isActiveStatus } from "~/models/Status";
import { isEmpty } from "~/utils/StringUtil";
import Styles from "../DealCard.module.scss";

type Props = {
	deal: Deal;
	handleMutate?: () => Promise<void>;
};

const notYetInput = <span className={Styles.NotYet}>未入力</span>;

const renderNameWithKana = (name: string | null, kana: string | null) => {
	if (isEmpty(name)) return notYetInput;

	return (
		<>
			<p>{name}</p>
			{!isEmpty(kana) && <p>（{kana}）</p>}
		</>
	);
};

const renderContact = (
	value: string | null,
	hrefPrefix: "tel:" | "mailto:"
) => {
	if (isEmpty(value)) return notYetInput;

	return (
		<a className={Styles.Link} href={hrefPrefix + value}>
			{value}
		</a>
	);
};

export function DealWitnessCard({ deal, handleMutate }: Props) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const showNone = deal.witnessType === "none";
	const title =
		deal.dealType === "restoration" ? "立会人情報" : "訪問時対応者情報";
	const witnessName = getWitnessName(deal);
	const witnessNameKana = getWitnessNameKana(deal);
	const witnessTel = getWitnessTel(deal);
	const witnessEmail = getWitnessEmail(deal);
	const canEdit = handleMutate !== undefined && isActiveStatus(deal.statusId);

	return (
		<div className={Styles.DealCard}>
			<h3 className={Styles.Title}>
				<RiUserLine className={Styles.TitleIcon} />
				{title}
				{canEdit ? (
					<div className={Styles.EditButtonWrapper}>
						<EditMenuButton
							onClick={() => {
								setIsModalOpen(true);
							}}
						>
							変更
						</EditMenuButton>
						{isModalOpen ? (
							<DealCardModal
								deal={deal}
								handleClose={() => {
									setIsModalOpen(false);
								}}
								handleMutate={handleMutate}
							/>
						) : null}
					</div>
				) : null}
			</h3>
			<div className={Styles.Content}>
				{!isEmpty(deal.inspectionScheduleAdjustmentNote) ? (
					<Alertv2 mb={designTokens["spacing/m"]} variant="warning">
						<Paragraph>
							日程調整時の注意事項があります。必ず確認の上、日程調整を行ってください。
						</Paragraph>
					</Alertv2>
				) : null}
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>日程調整時の注意事項</dt>
					<dd className={Styles.ListContentValue}>
						<p className={Styles.longSentence}>
							{isEmpty(deal.inspectionScheduleAdjustmentNote)
								? "なし"
								: deal.inspectionScheduleAdjustmentNote}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>氏名</dt>
					<dd className={Styles.ListContentValue}>
						{showNone
							? "なし"
							: renderNameWithKana(witnessName, witnessNameKana)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>電話番号</dt>
					<dd className={Styles.ListContentValue}>
						{showNone ? "なし" : renderContact(witnessTel, "tel:")}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>メールアドレス</dt>
					<dd className={Styles.ListContentValue}>
						{showNone ? "なし" : renderContact(witnessEmail, "mailto:")}
					</dd>
				</dl>
			</div>
		</div>
	);
}
